import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { AccountDetails, AssetBalance, AssetToken, BalanceMap, Issuer, NativeBalance, NativeToken } from '../stellar/wallet-sdk/types';
import { Networks, Transaction, Keypair } from '@stellar/stellar-sdk';
import { DataProvider } from '../stellar/wallet-sdk';
import BigNumber from 'bignumber.js';
import { Sep10ChallengeParams } from '../../types/types';
import * as Toml from '../stellar/tolm';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

const isTestnet = import.meta.env.VITE_isTestnet === 'true' ? true : false;
let apiProvider: DataProvider;
let apiProvider2: DataProvider;
let apiProvider3: DataProvider;

const initialIssuer: Issuer = {
  key: "any"
}

const initialAssetToken: AssetToken = {
  type: "credit_alphanum4",
  code: "any",
  issuer: initialIssuer,
}

const nativeToken: NativeToken = {
  type: "native",
  code: "XLM",
}

const initialassetBalance: AssetBalance = {
  available: new BigNumber(0),
  total: new BigNumber(0),
  buyingLiabilities: new BigNumber(0),
  sellingLiabilities: new BigNumber(0),
  token: initialAssetToken
}

const initialNativeBalance: NativeBalance = {
  token: nativeToken,
  total: new BigNumber(0),
  available: new BigNumber(0),
  buyingLiabilities: new BigNumber(0),
  sellingLiabilities: new BigNumber(0),
  minimumBalance: new BigNumber(0)
}

const initialBalanceState: BalanceMap = {

  "any:any": initialassetBalance,
  native: initialNativeBalance
}

let initialAccountDetails: AccountDetails = {
  id: '',
  subentryCount: 0,
  sponsoringCount: 0,
  sponsoredCount: 0,
  sponsor: '',
  inflationDestination: '',
  thresholds: {
    "low_threshold": 0,
    "med_threshold": 0,
    "high_threshold": 0,
  },
  signers: [],
  flags: {
    "auth_required": false,
    "auth_revocable": false,
    "auth_immutable": false,
    "auth_clawback_enabled": false

  },
  balances: initialBalanceState,
  sequenceNumber: ''
};

let currentStreamer: any = null; // A reference to store the current streamer

const switchApiProvider = (currentProvider: DataProvider) => {
  if (currentProvider === apiProvider) {
    return apiProvider2;
  } else if (currentProvider === apiProvider2) {
    return apiProvider3;
  } else {
    return apiProvider; // Default back to the first provider if needed
  }
}


const configureApiProvider = (stellarAccountId: string) => {

  apiProvider = new DataProvider({
    serverUrl: import.meta.env.VITE_HORIZON ? import.meta.env.VITE_HORIZON : 'https://horizon-testnet.stellar.org',
    accountOrKey: stellarAccountId,
    networkPassphrase: isTestnet ? Networks.TESTNET : Networks.PUBLIC,
  });

  apiProvider2 = new DataProvider({
    serverUrl: import.meta.env.VITE_HORIZON_PUBLIC_NODE ? import.meta.env.VITE_HORIZON_PUBLIC_NODE : 'https://horizon-testnet.stellar.org',
    accountOrKey: stellarAccountId,
    networkPassphrase: isTestnet ? Networks.TESTNET : Networks.PUBLIC,
  });

  apiProvider3 = new DataProvider({
    serverUrl: import.meta.env.VITE_HORIZON_LOBSTR ? import.meta.env.VITE_HORIZON_LOBSTR : 'https://horizon-testnet.stellar.org',
    accountOrKey: stellarAccountId,
    networkPassphrase: isTestnet ? Networks.TESTNET : Networks.PUBLIC,
  });

}

export const stellarApi = createApi({
  reducerPath: "stellar-api",
  baseQuery: fetchBaseQuery({ baseUrl: '/' }),
  endpoints: (builder) => ({

    accountDetails: builder.query({

      async queryFn(stellarAccountId) {

        configureApiProvider(stellarAccountId);

        return { data: { details: initialAccountDetails, isAccountFounded: false } };
      },

      async onCacheEntryAdded(stellarAccountId, { updateCachedData, cacheDataLoaded }) {

        // wait for the initial query to resolve before proceeding
        await cacheDataLoaded;

        let currentProvider: DataProvider = apiProvider;

        
        const watchAccountDetails = () => {

          // Close the existing streamer if it exists
          if (currentStreamer) {
            currentStreamer();
            currentStreamer = null;
          }

          currentStreamer = currentProvider.watchAccountDetails({

            onMessage: (updatedDetails: AccountDetails) => {
              updateCachedData((draft) => {
                if (updatedDetails.balances.native) {
                  const availableBalance = updatedDetails.balances.native.total.minus(updatedDetails.balances.native.minimumBalance).toNumber();
                  updatedDetails.balances.native.available = new BigNumber(availableBalance);
                }
                draft.details = updatedDetails;
                draft.isAccountFounded = true;
              });
            },

            onError: (err) => {

              if (err.status === 503) { // Check if the error is a 503
                
                //console.log('error switching providers..')
                
                // Close the current streamer before switching providers
                if (currentStreamer) {
                  currentStreamer();
                  currentStreamer = null;
                }

                currentProvider = switchApiProvider(currentProvider); // Switch the provider
                
                watchAccountDetails();
              
              } else {
                // Handle other errors, including NotFoundError and unfunded accounts
                if (err.name === 'NotFoundError' && err.isUnfunded) {
                  updateCachedData((draft) => {
                    draft.details.id = stellarAccountId;
                  });
                }
              }
            }

          });
        };

        watchAccountDetails(); // Start listening for account details

      },
    }),
    sep10: builder.query({

      async queryFn(params: Sep10ChallengeParams) {

        let token;

        const [error, tomlResponse] = await Toml.stellarTolmResolver(
          params.home_domain
        );

        if (error) {

          const err = error as Error;

          return { error: { status: 500, data: err.message } }

        }

        let authServerURL;
        let authServerKey;

        if (tomlResponse) {

          authServerURL = tomlResponse.WEB_AUTH_ENDPOINT;
          authServerKey = tomlResponse.SIGNING_KEY;

        }

        if (!authServerURL || !authServerKey) {

          const authError = new Error(`Missing auth server url or auth server key`);

          return { error: { status: 500, data: authError.message } }

        }

       let serverChallengeResponse: AxiosResponse<any, any>;

        serverChallengeResponse = await axios.get(`${authServerURL}`, {

          params: {
            account: params.account,
            memo: params.memo,
            client_domain: params.client_domain,
            home_domain: params.home_domain
          }

        })

        let challengeTx = serverChallengeResponse.data.transaction;
        const networkPassphrase = serverChallengeResponse.data.network_passphrase;

        // Throw error when network_passphrase is returned, and doesn't match
        if (params.network !== networkPassphrase) {

          const networPassPhraseError = new Error(`Network passphrase does not match: expected ${networkPassphrase}`)

          return { error: { status: 500, data: networPassPhraseError.message } }
        }

        // in case the client domain is available we need to sign the tx
        // with the ebioro server sep10 key.
        if (params.client_domain) {

          const axiosConfig: AxiosRequestConfig = {
            method: "post",
            url: `https://${params.client_domain}/wallet/client_domain/sign`,
            data: {
              transaction: challengeTx,
            },
            headers: {
              "Content-Type": "application/json",
              'Authorization': 'Bearer ' + `${params.client_domain_jwt}`

            }
          };

          try {

            const response = await axios.request(axiosConfig);
            const transaction = response.data.transaction;

            challengeTx = transaction


          } catch (error) {

            const err = error as AxiosError;

            console.log('the error ' + JSON.stringify(err))

          }


        }

        const stellarTx = new Transaction(challengeTx, networkPassphrase);
        const keypair = Keypair.fromSecret(params.privateKey);
        stellarTx.sign(keypair);

        const signedTx = stellarTx.toXDR();

        const axiosConfig: AxiosRequestConfig = {
          method: "post",
          url: `${authServerURL}`,
          data: {
            transaction: signedTx,
          },
          headers: { "Content-Type": "application/json" }
        };

        try {

          const serverTokenResponse = await axios.request(axiosConfig);

          token = serverTokenResponse.data.token;

          return { data: { [`${params.home_domain}`]: token } }

        } catch (error) {

          const err = error as AxiosError;

          return { error: { status: err.response?.status as number, data: err.response?.data || err.message } }


        }

      },

    })
  }),
})


export const { useAccountDetailsQuery, useSep10Query } = stellarApi